import { type ReactNode } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from '@/core/tamagoshi/Button/Button';
import { ButtonGroup } from '@/core/tamagoshi/ButtonGroup/ButtonGroup';
import { Modal } from '@/core/tamagoshi/Modal/Modal';
import { ModalContent } from '@/core/tamagoshi/Modal/ModalContent';
import { ModalFooter } from '@/core/tamagoshi/Modal/ModalFooter';
import { ModalHeader } from '@/core/tamagoshi/Modal/ModalHeader';
import {
  closeModalAction,
  openModalAction,
} from '@/domains/proB2BServices/Wishlists/store/Wishlists.actions';
import { useWishlistModalStateSelector } from '@/domains/proB2BServices/Wishlists/store/Wishlists.stateConsumer';
import { wishlistsSettings } from '@/domains/proB2BServices/Wishlists/wishlistsSettings';

import { returnButton } from './translations';

import styles from './modal.module.scss';

interface Props {
  title: string;
  modalId: `modal-${string}`;
  body: ReactNode;
  actionButtonText: string;
  onActionSuccess: (onModalClose?: () => void) => void;
  keepOpenOnSuccess?: boolean;
  onSecondaryButtonClick?: () => void;
  onActionClose?: () => void;
  showSecondaryButton?: boolean;
  performingAction?: boolean;
  disableMainCta?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg';
  secondaryButtonLabel?: string;
}

export const useInteractionModal = ({
  title,
  body,
  modalId,
  onSecondaryButtonClick = undefined,
  onActionSuccess,
  onActionClose,
  actionButtonText,
  keepOpenOnSuccess,
  showSecondaryButton = true,
  disableMainCta = false,
  performingAction = false,
  maxWidth = 'sm',
  secondaryButtonLabel = returnButton,
}: Props) => {
  const dispatch = useDispatch();
  const { theme } = wishlistsSettings;

  const onModalClose = () => {
    if (typeof onActionClose === 'function') onActionClose();
    dispatch(closeModalAction());
  };
  const onModalOpen = () => {
    dispatch(openModalAction(modalId));
  };

  const selectorModalId = useWishlistModalStateSelector();

  const isVisible = selectorModalId === modalId;

  const modal = isVisible ? (
    <Modal onClose={onModalClose} maxWidth={maxWidth}>
      <ModalHeader>{title}</ModalHeader>
      <ModalContent className={styles.modal}>
        <div id={modalId}>{body}</div>
      </ModalContent>
      <ModalFooter>
        <ButtonGroup isAutoMargin>
          {showSecondaryButton ? (
            <Button
              theme={theme}
              variant="secondary"
              onClick={() => {
                if (typeof onSecondaryButtonClick === 'function')
                  onSecondaryButtonClick();
                else onModalClose();
              }}
              data-testid="modal-back-button"
              className={styles.secondaryButton}
            >
              {secondaryButtonLabel}
            </Button>
          ) : null}
          <Button
            theme={theme}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            isLoading={performingAction}
            disabled={disableMainCta}
            onClick={() => {
              onActionSuccess(onModalClose);
              if (!keepOpenOnSuccess) onModalClose();
            }}
            data-testid="modal-confirm-button"
          >
            {actionButtonText}
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  ) : null;

  return {
    modal,
    onModalClose,
    onModalOpen,
  };
};
